'use client';

import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';

import { remarkDirectiveTags } from '../lib/remark-directive-tags';
import { remarkGithubAlerts } from '../lib/remark-github-alerts';
import { sanitizeSchema } from '../lib/sanitize-schema';
import { baseComponents } from './components/base-components';
import { Details, Summary } from './components/details';
import {
	InfoDirective,
	NoteDirective,
	TipDirective,
	WarningDirective,
} from './components/directive-callout';
import { reportComponents } from './components/report-components';
import { VideoEmbed } from './components/video-embed';

/**
 * Рендерер для теории и расширенного MD-контента.
 *
 * На текущий момент идентичен `ChatMarkdown`. Точка расширения — здесь будут
 * подключаться плагины для editorjs-совместимых блоков (тесты, многоязычный
 * код, аккордеоны), когда понадобится для миграции теории.
 */
const theoryComponents = {
	...baseComponents,
	details: Details,
	summary: Summary,
	'directive-note': NoteDirective,
	'directive-info': InfoDirective,
	'directive-tip': TipDirective,
	'directive-warning': WarningDirective,
} as unknown as Components;

const embedComponents = {
	...theoryComponents,
	img: VideoEmbed,
} as unknown as Components;

const reportDocumentComponents = {
	...theoryComponents,
	...reportComponents,
} as Components;

type TheoryMarkdownProps = {
	children: string;
	variant?: 'default' | 'report';
	/**
	 * Рендерить `![Заголовок](https://kinescope.io/...)` как встроенный плеер.
	 *
	 * Включаем только там, где Markdown пишем мы сами (вкладка «Видео-разбор»).
	 * Для AI-контента оставляем выключенным: sanitize-схему это не ослабляет,
	 * но и плеер из сгенерированной ссылки нам не нужен.
	 */
	allowVideoEmbeds?: boolean;
};

export function TheoryMarkdown({
	children,
	allowVideoEmbeds,
	variant = 'default',
}: TheoryMarkdownProps) {
	return (
		<div className="markdown-body">
			<ReactMarkdown
				remarkPlugins={[
					remarkGfm,
					remarkGithubAlerts,
					remarkDirective,
					remarkDirectiveTags,
				]}
				rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
				components={
					variant === 'report'
						? reportDocumentComponents
						: allowVideoEmbeds
							? embedComponents
							: theoryComponents
				}
			>
				{children}
			</ReactMarkdown>
		</div>
	);
}
